import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Plus, X, ImageIcon } from "lucide-react"
import { toast } from "sonner"
import MediaSelectorModal from "../library/media-selector"
import { updateProduct, getProductById } from "@/utils/helper/product"
import Loader from "@/components/loader/loader"

// Subcategory enums
const SUBCATEGORIES = {
  Skin: [
    "Serums",
    "Moisturizers",
    "Cleansers",
    "Masks",
    "Treatments",
    "Sun Care",
    "Toners",
    "Exfoliators",
    "Conditioners",
    "Oils",
    "Butters",
  ],
  Hair: [
    "Shampoos",
    "Conditioners",
    "Hair Oils",
    "Hair Masks",
    "Hair Treatments",
    "Hair Serums",
    "Styling Products",
    "Oils",
    "Butters",
  ],
}

export default function ProductEdit() {
  const { productId } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState({
    name: "",
    category: "",
    subcategory: "",
    description: "",
    ingredients: "",
    key_benefits: "",
    skin_type: "",
    usage_instructions: "",
    warnings: "",
    price: "",
    discount: "",
    stock_quantity: "",
    sku: "",
    variations: [],
    selectedImages: [],
  })

  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch product data on mount
  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true)
      try {
        const response = await getProductById(productId)
        // Handle different response structures (could be { product: {...} } or just {...})
        const data = response.product || response
        
        // Normalize category to match exactly "Skin" or "Hair"
        // Handle case-insensitive matching and whitespace
        // Check multiple possible locations for category data
        const rawCategory = (
          data.category_name || 
          data.category || 
          (data.categories && typeof data.categories === 'object' ? data.categories.name : null) ||
          ""
        ).trim()
        let normalizedCategory = ""
        if (rawCategory) {
          const lowerCategory = rawCategory.toLowerCase()
          if (lowerCategory === "skin") {
            normalizedCategory = "Skin"
          } else if (lowerCategory === "hair") {
            normalizedCategory = "Hair"
          }
        }
        
        // Normalize subcategory - ensure it matches one of the valid subcategories for the category
        const rawSubcategory = (data.subcategory || "").trim()
        let normalizedSubcategory = ""
        if (normalizedCategory && rawSubcategory) {
          // Check if the subcategory exists in the valid list for this category
          const validSubcategories = SUBCATEGORIES[normalizedCategory] || []
          const matchedSubcategory = validSubcategories.find(
            subcat => subcat.toLowerCase() === rawSubcategory.toLowerCase()
          )
          normalizedSubcategory = matchedSubcategory || ""
        }
        
        // Map backend data to form format
        setProduct({
          name: data.name || "",
          category: normalizedCategory || "",
          subcategory: normalizedSubcategory || "",
          description: data.description || "",
          ingredients: data.ingredients || "",
          key_benefits: data.key_benefits || "",
          skin_type: data.skin_type || "",
          usage_instructions: data.usage_instructions || "",
          warnings: data.warnings || "",
          price: data.price?.toString() || "",
          discount: data.discount?.toString() || "",
          stock_quantity: data.stock_quantity?.toString() || "",
          sku: data.sku || "",
          variations: data.variations && Array.isArray(data.variations) 
            ? data.variations.map((v) => ({
                size: v.size || "",
                scent: v.scent || "",
                price: v.price?.toString() || "",
                stock_quantity: v.stock_quantity?.toString() || "",
              }))
            : [],
          selectedImages: data.images && Array.isArray(data.images) && data.images.length > 0
            ? data.images.map((url, index) => ({
                id: `img-${index}-${Date.now()}`,
                url: url,
                name: `Image ${index + 1}`,
                type: "image",
              }))
            : data.thumbnail
            ? [{
                id: `img-0-${Date.now()}`,
                url: data.thumbnail,
                name: "Thumbnail",
                type: "image",
              }]
            : [],
        })
      } catch (error) {
        console.error("Error fetching product:", error)
        toast.error("Failed to load product. Please try again.")
        navigate("/admin/products/list")
      } finally {
        setIsLoading(false)
      }
    }

    if (productId) {
      fetchProduct()
    }
  }, [productId, navigate])

  const handleChange = (e) => {
    const { id, value } = e.target
    setProduct((prev) => ({ ...prev, [id]: value }))
  }

  const handleSelectChange = (id, value) => {
    setProduct((prev) => {
      const updated = { ...prev, [id]: value }
      // Clear subcategory when category changes
      if (id === "category") {
        updated.subcategory = ""
      }
      return updated
    })
  }

  const handleMediaSelect = (selectedFiles) => {
    if (Array.isArray(selectedFiles) && selectedFiles.length > 0) {
      setProduct((prev) => {
        const existingIds = new Set(prev.selectedImages.map((file) => file.id))
        const newFiles = selectedFiles.filter((file) => !existingIds.has(file.id))
        return {
          ...prev,
          selectedImages: [...prev.selectedImages, ...newFiles],
        }
      })
    }
  }

  const removeImage = (index) => {
    setProduct((prev) => ({
      ...prev,
      selectedImages: prev.selectedImages.filter((_, i) => i !== index),
    }))
  }

  const addVariation = () => {
    setProduct((prev) => ({
      ...prev,
      variations: [...prev.variations, { size: "", scent: "", price: "", stock_quantity: "" }],
    }))
  }

  const handleVariationChange = (index, field, value) => {
    const newVariations = [...product.variations]
    newVariations[index][field] = value
    setProduct((prev) => ({ ...prev, variations: newVariations }))
  }

  const removeVariation = (index) => {
    setProduct((prev) => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate required fields
      if (!product.name || !product.category || !product.price) {
        toast.error("Please fill in all required fields (Name, Category, Price)")
        setIsSubmitting(false)
        return
      }

      // Format imagePaths - extract URLs from imageFiles
      const imagePaths = product.selectedImages.map((file) => file.url).filter(Boolean)

      // Format variations - filter out empty variations and convert to backend format
      const validVariations = product.variations
        .filter((v) => v.size || v.scent || v.price || v.stock_quantity)
        .map((v) => ({
          size: v.size || null,
          scent: v.scent || null,
          price: v.price ? parseFloat(v.price) : 0,
          stock_quantity: v.stock_quantity ? parseInt(v.stock_quantity) : 0,
        }))

      // Prepare data for backend
      const productData = {
        name: product.name,
        category: product.category,
        subcategory: product.subcategory || null,
        description: product.description || null,
        ingredients: product.ingredients || null,
        key_benefits: product.key_benefits || null,
        skin_type: product.skin_type || null,
        usage_instructions: product.usage_instructions || null,
        warnings: product.warnings || null,
        price: parseFloat(product.price),
        discount: product.discount ? parseFloat(product.discount) : 0,
        stock_quantity: product.stock_quantity ? parseInt(product.stock_quantity) : 0,
        sku: product.sku || null,
        ...(validVariations.length > 0 && { variations: JSON.stringify(validVariations) }),
        ...(imagePaths.length > 0 && { imagePaths: JSON.stringify(imagePaths) }),
      }

      // Call API
      await updateProduct(productId, productData)
      
      toast.success("Product updated successfully!")
      
      // Navigate back to product list
      navigate("/admin/products/list")
    } catch (error) {
      console.error("Error updating product:", error)
      const errorMessage = error.response?.data?.error?.message || error.message || "Failed to update product. Please try again."
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
        <div className="w-full flex items-center justify-center py-12">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
      <div className="w-full space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Edit Product</h1>
          <p className="text-muted-foreground">Update the product details below.</p>
        </div>
        <div>
          <form onSubmit={handleSubmit} id="product-form" className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-5 rounded-none border-b bg-card">
                <TabsTrigger value="basic" className="rounded-none">Basic</TabsTrigger>
                <TabsTrigger value="pricing" className="rounded-none">Pricing</TabsTrigger>
                <TabsTrigger value="details" className="rounded-none">Details</TabsTrigger>
                <TabsTrigger value="images" className="rounded-none">Images</TabsTrigger>
                <TabsTrigger value="variations" className="rounded-none">Variations</TabsTrigger>
              </TabsList>

              {/* Basic Information Tab */}
              <TabsContent value="basic" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name" className="font-semibold text-foreground">
                        Product Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="e.g., Moisturizing Face Cream"
                        value={product.name}
                        onChange={handleChange}
                        maxLength={255}
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="category" className="font-semibold text-foreground">
                        Category <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={product.category || undefined}
                        onValueChange={(value) => handleSelectChange("category", value)}
                        required
                        key={`category-${product.category || 'empty'}`}
                      >
                        <SelectTrigger id="category" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Skin">Skin</SelectItem>
                          <SelectItem value="Hair">Hair</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Subcategory Field */}
                  {product.category && (
                    <div className="grid gap-2">
                      <Label htmlFor="subcategory" className="font-semibold text-foreground">
                        Subcategory (Optional)
                      </Label>
                      <Select
                        value={product.subcategory || "none"}
                        onValueChange={(value) => handleSelectChange("subcategory", value === "none" ? "" : value)}
                        key={`subcategory-${product.category}-${product.subcategory || 'none'}`}
                      >
                        <SelectTrigger id="subcategory" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select a subcategory (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {SUBCATEGORIES[product.category]?.map((subcat) => (
                            <SelectItem key={subcat} value={subcat}>
                              {subcat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="description" className="font-semibold text-foreground">
                      Description
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Detailed product description..."
                      value={product.description}
                      onChange={handleChange}
                      rows={4}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Pricing & Inventory Tab */}
              <TabsContent value="pricing" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Pricing & Inventory</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="price" className="font-semibold text-foreground">
                        Price (₦) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={product.price}
                        onChange={handleChange}
                        min="0"
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="discount" className="font-semibold text-foreground">
                        Discount (%)
                      </Label>
                      <Input
                        id="discount"
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={product.discount}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="stock_quantity" className="font-semibold text-foreground">
                        Stock Quantity <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="stock_quantity"
                        type="number"
                        placeholder="0"
                        value={product.stock_quantity}
                        onChange={handleChange}
                        min="0"
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="sku" className="font-semibold text-foreground">
                      SKU (Stock Keeping Unit)
                    </Label>
                    <Input
                      id="sku"
                      type="text"
                      placeholder="e.g., SKU-001"
                      value={product.sku}
                      onChange={handleChange}
                      maxLength={100}
                      className="border-border focus:ring-primary"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Product Details Tab */}
              <TabsContent value="details" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Details</h3>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="ingredients" className="font-semibold text-foreground">
                      Ingredients
                    </Label>
                    <Textarea
                      id="ingredients"
                      placeholder="List all ingredients..."
                      value={product.ingredients}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="key_benefits" className="font-semibold text-foreground">
                      Key Benefits
                    </Label>
                    <Textarea
                      id="key_benefits"
                      placeholder="e.g., Hydrates, Nourishes, Protects"
                      value={product.key_benefits}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="skin_type" className="font-semibold text-foreground">
                        Skin Type
                      </Label>
                      <Select
                        value={product.skin_type}
                        onValueChange={(value) => handleSelectChange("skin_type", value)}
                      >
                        <SelectTrigger id="skin_type" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select applicable skin types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Skin Types</SelectItem>
                          <SelectItem value="dry">Dry</SelectItem>
                          <SelectItem value="oily">Oily</SelectItem>
                          <SelectItem value="combination">Combination</SelectItem>
                          <SelectItem value="sensitive">Sensitive</SelectItem>
                          <SelectItem value="acne_prone">Acne-Prone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="usage_instructions" className="font-semibold text-foreground">
                      Usage Instructions
                    </Label>
                    <Textarea
                      id="usage_instructions"
                      placeholder="e.g., Apply twice daily..."
                      value={product.usage_instructions}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="warnings" className="font-semibold text-foreground">
                      Warnings
                    </Label>
                    <Textarea
                      id="warnings"
                      placeholder="e.g., For external use only"
                      value={product.warnings}
                      onChange={handleChange}
                      rows={2}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Product Images Tab */}
              <TabsContent value="images" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Images</h3>
                    <p className="text-sm text-muted-foreground">Upload up to 10 high-quality images</p>
                  </div>

                  <div className="border-2 border-dashed border-border rounded-lg p-8 hover:border-primary transition-colors cursor-pointer bg-card">
                    <div className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => setIsMediaModalOpen(true)}>
                      <div className="p-3 bg-muted rounded-lg">
                        <ImageIcon className="h-6 w-6 text-primary" />
                      </div>
                      <span className="font-semibold text-foreground">Select from Media Library</span>
                      <span className="text-sm text-muted-foreground">Choose images from your uploaded media</span>
                    </div>
                  </div>

                  {product.selectedImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {product.selectedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url || "/placeholder.svg"}
                            alt={`Selected image ${index + 1}`}
                            className="h-24 w-24 object-cover rounded-lg border border-border"
                            onError={(e) => {
                              e.target.src = "/placeholder.svg"
                            }}
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeImage(index)}
                          >
                            <X className="h-3 w-3" />
                            <span className="sr-only">Remove image</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Variations Tab */}
              <TabsContent value="variations" className="p-6">
                <div className="grid gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Variations</h3>
                    <p className="text-sm text-muted-foreground">Add different sizes, weights, or prices</p>
                  </div>

                  {product.variations.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No variations added yet</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {product.variations.map((variation, index) => (
                        <div
                          key={index}
                          className="p-4 border border-border rounded-lg hover:border-primary transition-colors bg-card"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Size</Label>
                              <Input
                                placeholder="e.g., 50ml"
                                value={variation.size}
                                onChange={(e) => handleVariationChange(index, "size", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Scent</Label>
                              <Input
                                placeholder="e.g., Lavender"
                                value={variation.scent}
                                onChange={(e) => handleVariationChange(index, "scent", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Price (₦)</Label>
                              <Input
                                placeholder="0.00"
                                type="number"
                                step="0.01"
                                value={variation.price}
                                onChange={(e) => handleVariationChange(index, "price", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Stock</Label>
                              <Input
                                placeholder="0"
                                type="number"
                                value={variation.stock_quantity}
                                onChange={(e) => handleVariationChange(index, "stock_quantity", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeVariation(index)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <X className="h-4 w-4" />
                              <span className="sr-only">Remove variation</span>
                            </Button>
                          </div>
                          <div className="grid gap-1 mt-3">
                            <Label className="text-xs font-semibold text-foreground">SKU</Label>
                            <Input
                              placeholder="e.g., SKU-001-50ml"
                              value={variation.sku}
                              onChange={(e) => handleVariationChange(index, "sku", e.target.value)}
                              className="border-border focus:ring-primary"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={addVariation}
                    variant="outline"
                    className="w-full border-border text-primary hover:bg-muted hover:text-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Variation
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate("/admin/products/list")} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2 inline-block" />
                    Updating...
                  </>
                ) : (
                  "Update Product"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Media Selector Modal */}
      <MediaSelectorModal
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        onSelect={handleMediaSelect}
        multiple={true}
        fileTypes={["image"]}
        title="Select Product Images"
        description="Choose one or more images from your media library"
      />
    </div>
  )
}

